import { makeAutoObservable, reaction, runInAction } from 'mobx';
import {
  enrollUserRequest,
  feedbackRequest,
  fetchRequestsInfo,
  getAnalytics,
  getMe,
  registerHumanizerEntry,
  getReferralDiscount,
  isTokenRejected,
  isUnreachable,
  keepABArmsRequest,
  setAccountLocaleRequest,
  setupAutoPayRequest,
} from 'requests';
import {pendingHumanizerEntry, acknowledgeHumanizerEntry} from 'humanizerEntry';
import { translate } from 'i18n/translator';
import {checkError, reprNetworkError, toSubKey} from 'utils';
import {logEvent} from 'analytics';
import {gtmEvent} from 'utils/gtm';
import {isFeatureEnabled, FEATURES} from 'config';
import {clearPersistedAuth, getCachedUser} from 'persistence';
import {identifyClarity} from 'clarity';
import {adoptAccountArms, clearLinkArms, keepRolledArms, pendingLinkArms, showsDiscountOffer, type ABArms} from 'utils/abTest';
import type { RootStore } from 'stores/rootStore';
import type { IStatsPeriod, IUserSettings } from 'types';

// Survey role ids (auth SurveyView); the "Prefer not to say" card is stored as
// 'none', 'prefer-not' or 'dont-share' depending on the survey version.
const HUMANIZER_ROLES = new Set(['student', 'writer', 'seo', 'none', 'prefer-not', 'dont-share']);

export type BootFailure = 'unreachable' | 'session_expired';

export class UserStore {
  root: RootStore;

  user: IUserSettings | null = null;
  token: string | null = null;
  /** The usage charts' buckets, per look-back window (days) — both sections at "7 days" share one load. */
  statistics: Record<number, IStatsPeriod[]> = {};

  initialized: boolean = false;
  /** The guest session could not be created (the IP is out of anons, or the endpoint failed).
   *  Nothing loads without a session, so the app stops waiting and offers the login instead. */
  anonAuthBlocked: boolean = false;
  /** Why the boot gave up (see BootFailedModal): the backend never answered it, retries
   *  included, or rejected the account's token. */
  bootFailure: BootFailure | null = null;
  authInProgress: boolean = false;
  /** The account is logging out, and the page load that ends every logout is underway. */
  loggingOut: boolean = false;
  /** Referral discount Stripe will apply at checkout, or null when there is none. */
  referralDiscount: { percent_off: number, months: number } | null = null;
  private referralDiscountAsked = false;
  private referralDiscountRequest: Promise<void> | null = null;
  /** Ordinal of the newest refresh, so a late older response can be discarded. */
  private refreshSeq = 0;
  /** The boot's /users/me has settled for the current token: later failures are silent blips. */
  private bootSettled = false;
  /** A rejected guest token was already swapped for a fresh guest session on this page. */
  private guestSessionReset = false;
  private localeSync: Promise<void> = Promise.resolve();
  private _lastFeedbackScanCount: number | null = null;
  private offeredArms = new Set<string>();
  /** Arms a tester's link moved the account to on this page (utils/abTest pendingLinkArms). */
  private linkArms: ABArms = {};

  constructor(root: RootStore) {
    this.root = root;
    this.user = getCachedUser();
    makeAutoObservable(this);
    // From the cached user on, so recordings are tagged from the first page — including the
    // routes that never run the app's init (/report/…).
    reaction(() => this.user?.uuid, identifyClarity, {fireImmediately: true});
  }

  get isAnonymous() {
    if (this.root.widgetMode || this.root.reportViewMode) return false;
    return this.user?.is_anon ?? true
  }

  /**
   * Whether the result is shown the anon's way: verdict and composition open, the per-sentence
   * breakdown behind a login. The widget keeps an anon's result open unless its host asked
   * for the app's flow.
   */
  get resultsGated() {
    if (this.root.reportViewMode || (this.root.widgetMode && !this.root.widgetAppFlow)) return false;
    return this.user?.is_anon ?? true
  }

  /** Whether a pre-run limit wall meets this session with the login rather than the paywall.
   *  isAnonymous reads false throughout the widget; its app flow walls an anon the app's way. */
  get wallsAsAnon() {
    return this.isAnonymous || (this.root.widgetAppFlow && !!this.user?.is_anon)
  }

  get scanCount() {
    return this.user?.scan_count ?? 0
  }

  /**
   * The word position a purchase is priced against (see utils/planCoverage).
   *
   * `granted_words_simple_scan` from /users/me already folds in the pay-as-you-go grant
   * (the backend merges `additional_limits` into it), which is why `remainingWords` below
   * needs no extra term — but a CANDIDATE plan's grant does not, so the wallet carries the
   * grant separately for it to add back.
   */
  get wordWallet() {
    const u = this.user;
    return {
      usedWords: u?.used_words_simple_scan ?? 0,
      // The Humanizer wallet's spend, for the same netting on the humanizerWords
      // dimension (limits.humanizer_words.used already folds the held words in).
      usedHumanizerWords: u?.limits?.humanizer_words?.used ?? 0,
      extraWords: u?.additional_granted_words_simple_scan ?? 0,
      // Buying from a paid plan modifies the existing subscription, and the backend resets
      // usage only for a brand-new one — so the words already spent carry over.
      // "Pay as you go" is not such a plan: it is what /users/me calls a Free account carrying
      // a granted word pack (User.subscription_name promotes the label off `additional_limits`).
      // Nothing sells it — the checkout type exists but no client asks for it, so on prod every
      // such account was granted by hand and has no Stripe subscription at all. A purchase there
      // starts a new one, and the usage does reset.
      isUpgrade: !this.isAnonymous && !this.isFree() && toSubKey(this.user?.subscription) !== 'pay_as_you_go',
    };
  }

  /** Words a free user can still scan this period (matches the sidebar "words left"). */
  get remainingWords() {
    const u = this.user;
    if (!u) return 0;
    return Math.max(0, (u.granted_words_simple_scan || 0) - (u.used_words_simple_scan || 0));
  }

  /** `?humanize=true` in the URL: the Humanizer past the policy below, for as long as the
   *  URL carries it — LayoutStore.setWorkspace drops it on the way out of the folder. */
  humanizerForced = false;

  forceHumanizer(forced: boolean) {
    this.humanizerForced = forced;
  }

  /** The Humanizer is open to an account whose post-signup survey role is in
   *  HUMANIZER_ROLES, and only on a stand where the feature is on, unless a landing entry grants durable access or the
   *  URL forces it. Every Humanizer surface (tab, history, plan cards, counters)
   *  reads the rule from here. */
  get humanizerAvailable(): boolean {
    if (this.humanizerForced || this.user?.humanizer_default_access || pendingHumanizerEntry()) return true;
    if (!isFeatureEnabled(FEATURES.HUMANIZER) || !this.user || this.user.is_anon) return false;
    // Normalised the way the backend normalises it (mailing context): the "Other"
    // card stores free text, so a typed "Student" counts the same as the card.
    const role = this.user.role?.trim().toLowerCase();
    return !!role && HUMANIZER_ROLES.has(role);
  }

  /** The plan's whole Humanizer allowance (the wallet's total once /users/me has it). */
  get grantedHumanizerWords() {
    return this.user?.limits?.humanizer_words?.total ?? this.user?.granted_words_humanizer ?? 0;
  }

  /** Humanizer words still available — what the backend would actually allow. */
  get remainingHumanizerWords() {
    return Math.max(0, this.grantedHumanizerWords - (this.user?.limits?.humanizer_words?.used ?? 0));
  }

  /** The most one humanize run takes, on every plan (the backend's HUMANIZER_MAX_WORDS);
   *  null until /users/me has said. */
  get humanizerRunCap(): number | null {
    return this.user?.limits?.humanizer_words?.per_run?.max ?? null;
  }

  /** The least one humanize run takes (the backend's HUMANIZER_MIN_WORDS); null until /users/me has said. */
  get humanizerRunFloor(): number | null {
    return this.user?.limits?.humanizer_words?.per_run?.min ?? null;
  }


  authAnonymous = async () => {
    try {
      const info = await fetchRequestsInfo();
      this.token = info.token;
      await this.root.refresh();
      this.initialized = true;
    } catch (e) {
      console.error('Anonymous auth failed', e);
      this.initialized = true;
    }
  };

  isFullyAuthenticated() {
    return this.isAuthenticated() && !this.isAnonymous;
  }

  setAnonAuthBlocked = (value: boolean) => {
    this.anonAuthBlocked = value;
  };

  get bootFailed() {
    return this.bootFailure !== null;
  }

  setBootFailure = (value: BootFailure | null) => {
    this.bootFailure = value;
  };

  setToken = (token: string) => {
    this.token = token;
    // A new token (a login, most likely) is a fresh boot.
    this.anonAuthBlocked = false;
    this.bootFailure = null;
    this.bootSettled = false;
    this.initialized = true;
  };

  auth = async (token: string) => {
    // Always apply the latest token synchronously so outgoing requests use it,
    // even if a previous refresh is still in flight.
    this.setToken(token);

    if (this.authInProgress) {
      // A refresh is already running — the outer auth() call will re-check
      // this.token after it finishes and refresh again if needed.
      return true;
    }

    this.authInProgress = true;
    try {
      let current = token;
      await this.root.refresh();
      // Token may have changed during refresh; keep refreshing until settled.
      while (this.token !== current && this.token !== null) {
        current = this.token;
        await this.root.refresh();
      }
      return true;
    } catch (e: any) {
      const errorInfo = reprNetworkError(e);

      if (checkError(errorInfo.code, 'authentication_failed')) {
        this.setError({
          message: () => translate('error-authentication-failed', {details: errorInfo.message}),
          code: errorInfo.code,
        });
        this.logout();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        this.setError({
          message: () => translate('error-network', {details: errorInfo.message}),
          code: errorInfo.code,
        });
      }
      return false;
    } finally {
      this.authInProgress = false;
    }
  };

  refresh = async () => {
    console.log('Refresh');
    // Several refreshes are in flight at once during boot (init, guest-checkout return,
    // an admin link applying its commands). Responses come back in any order, and an
    // older one carries the account as it was BEFORE the newer request's changes — the
    // word counter would visibly fall back. Only the newest answer may write.
    const seq = ++this.refreshSeq;
    const token = this.token;
    const boot = this.booting;
    try {
      const source = pendingHumanizerEntry();
      if (source) {
        // Save before /me so account hydration and OAuth draft restoration see the grant.
        // A failed save keeps the entry pending for the next refresh.
        try { await registerHumanizerEntry(source); } catch (error) { console.warn('Humanizer entry pending', error); }
      }
      const me = await getMe(boot);
      if (seq !== this.refreshSeq) return;
      this.user = me;
      if (source && me.humanizer_default_access && !me.is_anon) acknowledgeHumanizerEntry();
      this.followAccountArms();
      // A slower sibling request may have given up first.
      this.bootFailure = null;
    } catch (err) {
      console.log('Failed to refresh user');
      // A superseded request must not touch the error state either — setError()
      // with no argument CLEARS it, so a late failure would wipe an error a newer
      // refresh had just set. And if the newest request is the one that failed,
      // let the sequence fall back so an in-flight older response can still land:
      // otherwise every answer is discarded and `user` stays null forever, which
      // isReady() never recovers from.
      if (seq !== this.refreshSeq) return;
      this.refreshSeq -= 1;
      if (!!this.user) {
        this.setError();
      }
      if (boot) this.failBoot(err);
    } finally {
      // Startup is over for this token, answered or not; an answer for a token replaced
      // meanwhile (a login) settles nothing.
      if (this.token === token) this.bootSettled = true;
    }
  };

  /** The app's first /users/me for this token (the widget's token is its host's business). */
  private get booting() {
    return !this.bootSettled && !this.root.widgetMode;
  }

  private failBoot(err: unknown) {
    if (isTokenRejected(err)) {
      // A guest has no account to sign back into: init.js starts a fresh guest session instead —
      // once, since a fresh token rejected as well would only loop.
      if ((this.user && !this.user.is_anon) || this.guestSessionReset) {
        this.bootFailure = 'session_expired';
      } else {
        this.guestSessionReset = true;
        clearPersistedAuth();
        this.logout();
      }
      return;
    }
    // With a snapshot the page stays usable, so only an unreachable backend is worth the modal;
    // without one there is nothing to show.
    if (!this.user || isUnreachable(err)) this.bootFailure = 'unreachable';
  }

  /** No subscription to modify: Free and pay-as-you-go can still start one,
   *  which is what upgrade prompts and checkout-only offers hinge on. */
  get canStartSubscription() {
    const plan = toSubKey(this.user?.subscription);
    return plan === 'free' || plan === 'pay_as_you_go';
  }

  /** Ask once per session, from wherever a price is about to be quoted. Anons
   *  have no referral of their own yet — theirs is settled when they claim. */
  loadReferralDiscount = () => {
    if (this.referralDiscountRequest) return this.referralDiscountRequest;
    if (this.referralDiscountAsked || this.isAnonymous) return;
    this.referralDiscountAsked = true;
    const token = this.token;
    const pending = getReferralDiscount()
      .then((data) => {
        if (this.token !== token) return;
        this.referralDiscount = data.eligible && data.percent_off && data.months
          ? {percent_off: data.percent_off, months: data.months}
          : null;
      })
      .catch(() => {
        // A quote without the discount line is wrong by a line; a paywall that
        // fails to open is wrong by a sale — so the next quote asks again.
        if (this.token === token) this.referralDiscountAsked = false;
      })
      .finally(() => {
        if (this.referralDiscountRequest === pending) this.referralDiscountRequest = null;
      });
    this.referralDiscountRequest = pending;
    return pending;
  };

  /** An account is in the same A/B arms in every browser it signs in from (utils/abTest): its
   *  arms go over the cookies, and an arm this browser has that it lacks, rolled now or later,
   *  goes to the account. `is_anon`, not isAnonymous: a report view forces that one. */
  private followAccountArms() {
    if (!this.user || this.user.is_anon) return keepRolledArms(null);
    keepRolledArms(this.keepABArms);
    const linked = pendingLinkArms();
    if (Object.keys(linked).length) {
      this.linkArms = {...this.linkArms, ...linked};
      // Cleared only once the account has them: a failed force retries on the next load.
      this.offerArms(linked, true, () => clearLinkArms(Object.keys(linked)));
    }
    this.keepABArms(this.adoptArms(this.user.ab_arms));
  }

  // A /users/me or keep answer that left before the link's force landed still names the old arm.
  private adoptArms = (arms: ABArms | null | undefined) => adoptAccountArms({...arms, ...this.linkArms});

  // The browser follows the account's answer, so the first browser to roll an arm decides it.
  // Each test is offered once per page: where cookies don't stick (a cross-site iframe) every
  // read rolls anew, and a refused or failed offer waits for the next load.
  private keepABArms = (arms: ABArms) => {
    const fresh = Object.entries(arms).filter(([cookie]) => !this.offeredArms.has(cookie));
    if (!fresh.length) return;
    fresh.forEach(([cookie]) => this.offeredArms.add(cookie));
    this.offerArms(Object.fromEntries(fresh));
  };

  private offerArms(arms: ABArms, force = false, onKept?: () => void) {
    const token = this.token;
    keepABArmsRequest(arms, force)
      .then((kept) => {
        onKept?.();
        if (this.token === token) this.adoptArms(kept);
      })
      .catch(() => {});
  }

  setLoggingOut() {
    this.loggingOut = true;
  }

  logout() {
    this.user = null;
    this.token = null;
    this.referralDiscount = null;
    this.referralDiscountAsked = false;
    this.referralDiscountRequest = null;
    keepRolledArms(null);
    this.offeredArms.clear();
    this.linkArms = {};
  }

  setError(error?: any) {
    this.root.setError(error || null);
  }

  isAuthenticated() {
    return this.token != null;
  }

  isReady() {
    return this.isAuthenticated() && this.user !== null && this.initialized;
  }

  /** The account's stored language is not the one the UI is in. Judged only after the boot's
   *  /users/me has settled — the cached snapshot may predate the last switch — and only when
   *  the backend reports the field at all (`undefined`, unlike `null`, means it does not). */
  needsLocaleSync(lang: string) {
    const user = this.user;
    return this.bootSettled && !!user && !user.is_anon && user.locale !== undefined && user.locale !== lang;
  }

  /** One request at a time, so the account ends on the last switch. Best-effort: a failed
   *  attempt is asked again on the next page load. */
  syncLocale = (lang: string) => {
    const token = this.token;
    this.localeSync = this.localeSync.then(async () => {
      if (this.token !== token) return;
      try {
        await setAccountLocaleRequest(lang);
      } catch {
        return;
      }
      runInAction(() => {
        if (this.token === token && this.user) this.user.locale = lang;
      });
    });
    return this.localeSync;
  };

  isFree() {
    return this.user?.subscription === 'Free';
  }

  /** When the running trial ends, as a date — null while no trial runs or the server named
   *  no (readable) end. The one place the trial status surfaces get both facts from. */
  get trialEnd(): Date | null {
    if (!this.user?.is_trialing || !this.user.trial_end) return null;
    const end = new Date(this.user.trial_end);
    return isNaN(end.getTime()) ? null : end;
  }

  /** A trial is running and the server named its end — what the trial status surfaces show. */
  get isTrialing() {
    return this.trialEnd !== null;
  }

  /** Whether this account may download scan certificates — the plan permission the
   *  backend gates the PDF on, so the wall in front of every download control reads
   *  the same flag the server enforces. False only on an anon outside a paid plan; true
   *  on every registered account and on the standalone tools (aichecker / detector),
   *  whose users are anonymous by design and must keep their certificates. */
  get canGenerateReport() {
    return !!this.user?.can_generate_report;
  }

  isEnterprise() {
    return toSubKey(this.user?.subscription) === 'enterprise';
  }

  /** How long the discounted annual price still stands, 0 when it doesn't.
   *  The deadline is the server's (`discount_available_until` on /users/me) and the
   *  checkout refuses a discount past it, so the promo shown here is the promo charged —
   *  who qualifies and for how long is decided once, on the side that takes the money.
   *
   *  The A/B arm is the second half of the answer: the trial and this promo are the two
   *  arms of one experiment (see utils/abTest showsDiscountOffer), so the promo runs for
   *  the control arm and for nobody else — the cookie decides that, not the account's
   *  remaining trial. A trial-arm account that has already used its trial holds no promo
   *  either; it is out of offers, not moved to the other arm. Every surface that draws the
   *  discount — paywall, subscription page, sidebar, top bar — and the checkout request
   *  itself count down from here, so the split is applied once, in the only place all of
   *  them agree on.
   *
   *  The cohort is read only when there is a window to hide, which keeps the exposure
   *  cookie out of pages that show nothing: a window exists only for an account stamped at
   *  the paywall, and the paywall assigns the cohort before it stamps. */
  getDiscountRemainingMs(): number {
    const until = this.user?.discount_available_until;
    if (!until) return 0;
    const end = Date.parse(until);
    if (isNaN(end)) return 0;
    const remaining = Math.max(0, end - Date.now());
    if (remaining > 0 && !showsDiscountOffer()) return 0;
    return remaining;
  }

  isDiscountActive(): boolean {
    return this.getDiscountRemainingMs() > 0;
  }

  enrollUser = async () => {
    await enrollUserRequest();
  };

  // Only marks the account enrolled. It used to also file a free-Premium
  // SubscribeRequest, which the backend retired in Aug 2026 (it granted
  // Premium to anyone who called it) — enrolling itself grants nothing.
  /** Resolves whether the account got enrolled; a failure is told in a toast. */
  enroll = async (): Promise<boolean> => {
    try {
      await this.enrollUser();
    } catch (e) {
      this.root.toast.error('free-premium-enroll-error', e);
      return false;
    }
    await logEvent(gtmEvent('GTM_free_premium_enroll', {page_type: 'app_scan', entrypoint: 'free_premium_popup'}));
    await this.refresh();
    return true;
  };

  /** The feedback modal's answer: a rating with its comment, or `rejected` — the user
   *  asked not to be asked again. A failure is told in a toast. */
  sendFeedback = async ({rejected, rating, message}: {rejected?: boolean, rating?: number, message?: string}) => {
    try {
      await feedbackRequest(rejected, rating, message);
    } catch (e) {
      this.root.toast.error('feedback-send-error', e);
      return;
    }
    await this.refresh();
  };

  isEnrolled() {
    return !!this.user?.enrolled;
  }

  shouldShowFeedbackModal() {
    if (this.user?.feedback_provided) return false;
    if (this._lastFeedbackScanCount === this.scanCount) return false;

    let shouldShow = false;
    if (toSubKey(this.user?.subscription) == "detector_tool") {
      if ([15 - 1, 30 - 1, 50 - 1].includes(this.scanCount)) shouldShow = true;
    } else {
      if ([5 - 1, 10 - 1, 30 - 1, 50 - 1].includes(this.scanCount)) shouldShow = true;
      if (this.scanCount > 50 && this.scanCount % 50 === 49) shouldShow = true;
    }

    if (shouldShow) {
      this._lastFeedbackScanCount = this.scanCount;
    }
    return shouldShow;
  }

  shouldShowEnrollModal() {
    if (!isFeatureEnabled(FEATURES.FREE_PREMIUM)) return false;
    return !this.isEnrolled() && this.isFree() && [10 - 1, 30 - 1, 50 - 1].includes(this.scanCount);
  }

  private statisticsLoads: Partial<Record<number, Promise<void>>> = {};

  loadStatistics = (threshold: number) => {
    // The page's sections mount on the same window — one request serves them all.
    const inFlight = this.statisticsLoads[threshold];
    if (inFlight) return inFlight;
    const load = (async () => {
      try {
        const periods = await getAnalytics(threshold);
        runInAction(() => {
          this.statistics = {...this.statistics, [threshold]: periods};
        });
      } catch (e) {
        this.root.toast.error('statistics-load-error', e);
      } finally {
        delete this.statisticsLoads[threshold];
      }
    })();
    this.statisticsLoads[threshold] = load;
    return load;
  };

  setupAutoPay = async (enabled: boolean, limit: number, addAmount: number) => {
    await setupAutoPayRequest(enabled, limit, addAmount);
    await this.root.subscription.loadPaymentDetails();
  };

  shouldRedirectToLogin = (): boolean => {
    const user = this.user;
    if (!user) {
      return false;
    }
    if (user.is_anon) {
      return false;
    }
    const GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;
    const joined = Date.parse(user.date_joined);
    if (Date.now() - joined > GRACE_PERIOD_MS) {
      return false;
    }
    if (user.policy_accepted) {
      return false;
    }
    return true;
  }
}
